import { parse } from './urlHelpers';
import { parseDateFromISO8601 } from './dates';
import { EVENT_DATES_QUERY_PARAM, parseEventDatesParam } from './eventDateRangeFilter';
import { parseSelectFilterOptions } from './search';
import {
  LISTING_TYPE_TO_EVENT_TYPE_ID,
  PRIMARY_EVENT_SHARETRIBE_ENUM_PUBLIC_DATA_KEY,
  SHARETRIBE_ENUM_OPTION_TO_EVENT_TYPE_ID,
} from '../config/primaryEventDiscovery';

const MAX_SEARCH_TEXT = 200;

const toPromotixMdYyyy = date => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

const normalizeListingTypes = pubListingType => {
  if (pubListingType == null || pubListingType === '') {
    return [];
  }
  if (Array.isArray(pubListingType)) {
    return pubListingType.flatMap(v => parseSelectFilterOptions(String(v))).map(s => s.trim()).filter(Boolean);
  }
  return parseSelectFilterOptions(String(pubListingType))
    .map(s => s.trim())
    .filter(Boolean);
};

/**
 * Maps Sharetribe filter values → single PromoTix `event_type_id` when possible.
 * If several distinct ids match (multi-select), returns the first so upstream still gets a filter.
 */
export const resolveEventTypeIdFromListingTypes = (pubListingTypeParam, mapping = LISTING_TYPE_TO_EVENT_TYPE_ID) => {
  if (!mapping || !Object.keys(mapping).length) {
    return null;
  }
  const types = normalizeListingTypes(pubListingTypeParam);
  if (!types.length) {
    return null;
  }
  const ids = types.map(t => mapping[t]).filter(v => v != null && Number.isFinite(Number(v)));
  if (!ids.length) {
    return null;
  }
  const asNums = ids.map(Number);
  const unique = [...new Set(asNums)];
  return String(unique[0]);
};

const firstEventTypeIdFromQuery = q => {
  if (!q || typeof q !== 'object') {
    return null;
  }
  const explicit = q.event_type_id != null && q.event_type_id !== '' ? String(q.event_type_id) : null;
  if (explicit) {
    return explicit;
  }
  const enumKey = PRIMARY_EVENT_SHARETRIBE_ENUM_PUBLIC_DATA_KEY;
  if (enumKey) {
    const fromConfigured = resolveEventTypeIdFromListingTypes(
      q[`pub_${enumKey}`],
      SHARETRIBE_ENUM_OPTION_TO_EVENT_TYPE_ID
    );
    if (fromConfigured) {
      return fromConfigured;
    }
  }
  const fromListingType = resolveEventTypeIdFromListingTypes(q.pub_listingType, LISTING_TYPE_TO_EVENT_TYPE_ID);
  if (fromListingType) {
    return fromListingType;
  }
  const fromCategory = resolveEventTypeIdFromListingTypes(q.pub_category, SHARETRIBE_ENUM_OPTION_TO_EVENT_TYPE_ID);
  if (fromCategory) {
    return fromCategory;
  }
  for (const key of Object.keys(q)) {
    if (!key.startsWith('pub_')) {
      continue;
    }
    const fromEnum = resolveEventTypeIdFromListingTypes(q[key], SHARETRIBE_ENUM_OPTION_TO_EVENT_TYPE_ID);
    if (fromEnum) {
      return fromEnum;
    }
  }
  return null;
};

const normalizeLocationSearch = locationSearch => {
  if (locationSearch == null) {
    return '';
  }
  return typeof locationSearch === 'string' ? locationSearch : String(locationSearch);
};

const primaryDateRangeFromSearch = locationSearch => {
  const loc = normalizeLocationSearch(locationSearch);
  const q = parse(loc, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });
  const fromEventDates = parseEventDatesParam(q[EVENT_DATES_QUERY_PARAM]);
  if (fromEventDates) {
    const [sy, sm, sd] = fromEventDates.startDay.split('-').map(Number);
    const [ey, em, ed] = fromEventDates.endDay.split('-').map(Number);
    return {
      search_start_date: toPromotixMdYyyy(new Date(sy, sm - 1, sd)),
      search_end_date: toPromotixMdYyyy(new Date(ey, em - 1, ed)),
    };
  }
  const raw = q.dates;
  if (raw == null || raw === '') {
    return { search_start_date: null, search_end_date: null };
  }
  const values = String(raw).split(',');
  if (values.length !== 2) {
    return { search_start_date: null, search_end_date: null };
  }
  const startDate = parseDateFromISO8601(values[0]);
  const endDate = parseDateFromISO8601(values[1]);
  return {
    search_start_date: toPromotixMdYyyy(startDate),
    search_end_date: toPromotixMdYyyy(endDate),
  };
};

/**
 * Build query string for GET /api/primary-events from the search page URL (?…).
 * @param {string} locationSearch `location.search` or equivalent
 * @param {{ pageNoOverride?: number|string }} [overrides] When set, `page_no` uses this value
 *   instead of the Search URL `page` (used when aggregating all upstream pages).
 */
export const buildPrimaryEventsClientQuery = (locationSearch, overrides = {}) => {
  const loc = normalizeLocationSearch(locationSearch);
  const q = parse(loc, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const params = new URLSearchParams();
  const timezone =
    typeof Intl !== 'undefined' && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'America/New_York';
  params.set('timezone', timezone);

  const pageNo =
    overrides.pageNoOverride != null && overrides.pageNoOverride !== ''
      ? String(overrides.pageNoOverride)
      : q.page != null && q.page !== ''
      ? String(q.page)
      : '1';
  params.set('page_no', pageNo);
  params.set('record_per_page', '24');

  if (q.keywords != null && String(q.keywords).trim()) {
    params.set('search_text', String(q.keywords).trim().slice(0, MAX_SEARCH_TEXT));
  }

  const { search_start_date, search_end_date } = primaryDateRangeFromSearch(loc);
  if (search_start_date && search_end_date) {
    params.set('search_start_date', search_start_date);
    params.set('search_end_date', search_end_date);
  }

  const resolvedEventTypeId = firstEventTypeIdFromQuery(q);
  if (resolvedEventTypeId) {
    params.set('event_type_id', resolvedEventTypeId);
  }

  return params.toString();
};

const upstreamAllEventsBase = () => {
  const raw =
    (typeof process !== 'undefined' && process.env.REACT_APP_PROMOTIX_PRIMARY_API_BASE_URL) ||
    'https://api.promotix.com/api/v1';
  return raw.replace(/\/+$/, '');
};

import { sameOriginApiBase } from './sameOriginApiBase';

const jsonHeaders = () => {
  const h = { Accept: 'application/json', 'Cache-Control': 'no-cache' };
  // Browsers forbid setting User-Agent on fetch; Node SSR may set it.
  if (typeof window === 'undefined') {
    h['User-Agent'] = 'PromoTix-Reseller-Discovery/1.0';
  }
  return h;
};

/**
 * Fetch the full pre-warmed event catalog from the server in a single request.
 * Returns the same { success, data: { events, total_record, ... } } shape.
 */
export const fetchPrimaryEventsCatalog = () => {
  const origin = sameOriginApiBase();
  const url = origin ? `${origin}/api/primary-events/catalog` : null;
  if (!url) {
    return Promise.reject(new Error('Cannot determine catalog URL'));
  }
  return fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: jsonHeaders(),
  }).then(res =>
    // Read as text first so a non-JSON body (e.g. an HTML error page from a WAF/CDN
    // edge) surfaces as a clean error instead of a JSON.parse SyntaxError.
    res.text().then(text => {
      if (!res.ok) {
        const err = new Error(`Catalog HTTP ${res.status}`);
        err.status = res.status;
        throw err;
      }
      try {
        return text ? JSON.parse(text) : null;
      } catch (parseErr) {
        const err = new Error(
          `Catalog non-JSON response (${res.status}): ${String(text).slice(0, 120)}`
        );
        err.status = res.status;
        throw err;
      }
    })
  );
};

/**
 * Loads primary events. Prefers same-origin `/api/primary-events` (server geocoding, whitelist).
 * If that fails or `REACT_APP_PROMOTIX_DISCOVERY_PRIMARY_FETCH=upstream`, calls PromoTix
 * public `all_events` directly (CORS `*`). Geocoding is only applied on the proxy path.
 */
export const fetchPrimaryEventsViaProxy = (locationSearch, queryOverrides = {}) => {
  const qs = buildPrimaryEventsClientQuery(locationSearch, queryOverrides);
  const upstreamUrl = `${upstreamAllEventsBase()}/attendee/all_events?${qs}`;
  const fetchOptsUpstream = {
    method: 'GET',
    credentials: 'omit',
    headers: jsonHeaders(),
  };

  const fetchMode =
    typeof process !== 'undefined' && process.env.REACT_APP_PROMOTIX_DISCOVERY_PRIMARY_FETCH
      ? String(process.env.REACT_APP_PROMOTIX_DISCOVERY_PRIMARY_FETCH).trim().toLowerCase()
      : '';
  const forceUpstream = fetchMode === 'upstream';

  if (forceUpstream) {
    return fetch(upstreamUrl, fetchOptsUpstream).then(res => {
      if (!res.ok) {
        const err = new Error(`Primary events upstream HTTP ${res.status}`);
        err.status = res.status;
        throw err;
      }
      return res.json();
    });
  }

  const origin = sameOriginApiBase();
  const proxyUrl = origin ? `${origin}/api/primary-events?${qs}` : null;

  const fetchOptsProxy = {
    method: 'GET',
    credentials: 'include',
    headers: jsonHeaders(),
  };

  const tryUpstream = () =>
    fetch(upstreamUrl, fetchOptsUpstream).then(res => {
      if (!res.ok) {
        const err = new Error(`Primary events upstream HTTP ${res.status}`);
        err.status = res.status;
        throw err;
      }
      return res.json();
    });

  if (!proxyUrl) {
    return tryUpstream();
  }

  return fetch(proxyUrl, fetchOptsProxy)
    .then(res => {
      if (res.ok) {
        return res.json();
      }
      // eslint-disable-next-line no-console
      console.warn(
        `[Discovery] proxy /api/primary-events returned ${res.status}; falling back to upstream`
      );
      return tryUpstream();
    })
    .catch(proxyErr => {
      // Network error, DNS failure, TLS issue, or timeout — proxy unreachable.
      // Fall back to the public PromoTix API so events still load without geocoding.
      // eslint-disable-next-line no-console
      console.warn(
        '[Discovery] proxy /api/primary-events fetch threw; falling back to upstream:',
        proxyErr && proxyErr.message ? proxyErr.message : proxyErr
      );
      return tryUpstream();
    });
};
